// GanttConnections.jsx
import React, { useMemo } from 'react';

const PALETA_KOLOROW = [
  '#3f51b5', '#f4a261', '#e63946', '#2a9d8f', '#9c27b0',
  '#e91e63', '#00bcd4', '#ff9800', '#8bc34a', '#795548',
];

const KOLOR_DOMYSLNY = '#888888';

const czyMajaWspolnyArkusz = (arkuszeA, arkuszeB) => {
  if (arkuszeA === null || arkuszeA === undefined || arkuszeB === null || arkuszeB === undefined) {
    return false;
  }

  const listA = Array.isArray(arkuszeA)
    ? arkuszeA.map(String)
    : String(arkuszeA).split(',').map(s => s.trim()).filter(Boolean);

  const listB = Array.isArray(arkuszeB)
    ? arkuszeB.map(String)
    : String(arkuszeB).split(',').map(s => s.trim()).filter(Boolean);

  return listA.some(arkusz => listB.includes(arkusz));
};

const GanttConnections = ({
  groupedStages = {},
  processorIds = [],
  rowYPositions = {},
  minDate,
  maxDate,
  totalChartWidth,
  selectedZamowienieId,
  showAllConections,
  showElementConections,
  calculateBarProperties
}) => {
  const connectionLines = useMemo(() => {
    // DIAGNOSTYKA PRODUKCYJNA - SPRAWDZENIE WARUNKÓW WEJŚCIOWYCH
    if (!showAllConections && !showElementConections && !selectedZamowienieId) {
      console.warn('[GanttConnections PROD] Brak włączonego trybu połączeń oraz braku wybranego zamówienia.');
      return [];
    }

    if (!minDate || !maxDate || !totalChartWidth) {
      console.warn('[GanttConnections PROD] Brak wymiarów/dat:', { minDate, maxDate, totalChartWidth });
      return [];
    }

    if (typeof calculateBarProperties !== 'function') {
      console.error('[GanttConnections PROD] calculateBarProperties nie jest funkcją!', calculateBarProperties);
      return [];
    }

    const stagesByGroupAndIndex = {};
    const elementColorMap = {};

    const selectedZamowienieIdStr = selectedZamowienieId !== undefined && selectedZamowienieId !== null 
      ? String(selectedZamowienieId) 
      : null;

    let processedStagesCount = 0;

    processorIds.forEach((processorId) => {
      const rowStages = groupedStages[processorId] || [];
      const y = rowYPositions[processorId] ?? 0;

      rowStages.forEach((stage) => {
        if (!stage || stage.zamowienie_id === undefined || stage.zamowienie_id === null) return;

        const zamowienieIdStr = String(stage.zamowienie_id);

        if (showElementConections) {
          if (zamowienieIdStr !== selectedZamowienieIdStr) return;
          if (stage.element_id === undefined || stage.element_id === null) return;
        } else if (!showAllConections && zamowienieIdStr !== selectedZamowienieIdStr) {
          return;
        }

        processedStagesCount++;

        const elementIdStr = stage.element_id !== undefined && stage.element_id !== null 
          ? String(stage.element_id) 
          : null;

        if (showElementConections && elementIdStr && !(elementIdStr in elementColorMap)) {
          const currentIndex = Object.keys(elementColorMap).length;
          elementColorMap[elementIdStr] = currentIndex < PALETA_KOLOROW.length
            ? PALETA_KOLOROW[currentIndex]
            : KOLOR_DOMYSLNY;
        }

        const groupKey = showElementConections
          ? `elem_${zamowienieIdStr}_${elementIdStr}`
          : `zam_${zamowienieIdStr}`;

        const indeks = Number(stage.indeks_procesu) || 0;
        
        // Zabezpieczenie przed NaN z funkcji przeliczającej pozycję
        const barProps = calculateBarProperties(stage.start, stage.end);
        const leftPx = Number(barProps?.leftPx);
        const widthPx = Number(barProps?.widthPx);

        if (Number.isNaN(leftPx) || Number.isNaN(widthPx)) {
          console.error('[GanttConnections PROD] calculateBarProperties zwróciło NaN dla etapu:', stage);
          return;
        }

        if (!stagesByGroupAndIndex[groupKey]) {
          stagesByGroupAndIndex[groupKey] = {};
        }

        if (!stagesByGroupAndIndex[groupKey][indeks]) {
          stagesByGroupAndIndex[groupKey][indeks] = [];
        }

        stagesByGroupAndIndex[groupKey][indeks].push({
          stageId: String(stage.global_id || stage.id),
          zamowienieId: zamowienieIdStr,
          elementId: elementIdStr,
          arkuszeId: stage.arkusze_id,
          indeksProcesu: indeks,
          outPoint: { x: leftPx + widthPx, y },
          inPoint: { x: leftPx, y }
        });
      });
    });

    if (processedStagesCount === 0) {
      console.warn('[GanttConnections PROD] Pasujące etapy: 0 (sprawdź czy zamowienie_id z API pokrywa się z wybranym)');
      return [];
    }

    const lines = [];

    Object.keys(stagesByGroupAndIndex).forEach((groupKey) => {
      const indexesMap = stagesByGroupAndIndex[groupKey];
      
      const sortedIndexes = Object.keys(indexesMap)
        .map(Number)
        .sort((a, b) => a - b);

      for (let i = 0; i < sortedIndexes.length - 1; i++) {
        const currentIndeks = sortedIndexes[i];
        const nextIndeks = sortedIndexes[i + 1];

        const currentStages = indexesMap[currentIndeks];
        const nextStages = indexesMap[nextIndeks];

        currentStages.forEach((fromStage) => {
          nextStages.forEach((toStage) => {
            if (!czyMajaWspolnyArkusz(fromStage.arkuszeId, toStage.arkuszeId)) {
              return;
            }

            const strokeColor = showElementConections
              ? (elementColorMap[fromStage.elementId] || KOLOR_DOMYSLNY)
              : '#4d9aff';

            lines.push({
              id: `${groupKey}_${fromStage.stageId}_to_${toStage.stageId}`,
              zamowienieId: fromStage.zamowienieId,
              elementId: fromStage.elementId,
              isSelected: fromStage.zamowienieId === selectedZamowienieIdStr,
              from: fromStage.outPoint,
              to: toStage.inPoint,
              strokeColor,
              type: showElementConections ? 'element' : 'order'
            });
          });
        });
      }
    });

    console.log(`[GanttConnections PROD] Wygenerowano połączeń: ${lines.length}`);
    return lines;
  }, [
    groupedStages,
    processorIds,
    rowYPositions,
    minDate,
    maxDate,
    totalChartWidth,
    selectedZamowienieId,
    showAllConections,
    showElementConections,
    calculateBarProperties
  ]);

  if (connectionLines.length === 0) return null;

  const uniqueElements = Array.from(
    new Map(
      connectionLines
        .filter(line => line.elementId !== null && line.elementId !== undefined)
        .map(line => [line.elementId, line.strokeColor])
    ).entries()
  );

  return (
    <svg
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        zIndex: 10
      }}
    >
      <defs>
        <marker id="arrowhead-default" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto">
          <polygon points="0 0, 6 3, 0 6" fill="#4d9aff" />
        </marker>
        <marker id="arrowhead-selected" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto">
          <polygon points="0 0, 6 3, 0 6" fill="#ff4d4f" />
        </marker>

        {uniqueElements.map(([elementId, color]) => (
          <marker
            key={`marker-${elementId}`}
            id={`arrowhead-elem-${elementId}`}
            markerWidth="6"
            markerHeight="6"
            refX="5"
            refY="3"
            orient="auto"
          >
            <polygon points="0 0, 6 3, 0 6" fill={color} />
          </marker>
        ))}
      </defs>

      {connectionLines.map((line) => {
        const { from, to, isSelected, id, type, strokeColor, elementId } = line;
        const deltaX = Math.max(20, Math.abs(to.x - from.x) / 2);
        const pathData = `M ${from.x} ${from.y} C ${from.x + deltaX} ${from.y}, ${to.x - deltaX} ${to.y}, ${to.x} ${to.y}`;

        let finalColor = strokeColor;
        let markerId = `url(#arrowhead-elem-${elementId})`;

        if (!showElementConections && isSelected) {
          finalColor = '#ff4d4f';
          markerId = 'url(#arrowhead-selected)';
        } else if (!showElementConections) {
          markerId = 'url(#arrowhead-default)';
        }

        return (
          <g key={`connection-${id}`}>
            <path
              d={pathData}
              fill="none"
              stroke={finalColor}
              strokeWidth="1"
              strokeDasharray={type === 'element' ? 'none' : '5 3'}
              markerEnd={markerId}
            />
            <circle cx={from.x} cy={from.y} r="3.5" fill={finalColor} />
          </g>
        );
      })}
    </svg>
  );
};

export default React.memo(GanttConnections);